import { Box, Button, Stack, Typography } from "@mui/material";
import type { SpecsHistoryRevision } from "../../service/SpecsRevisionService";
import { createStyles, palette } from "../../styles/theme";

export type RevisionDiffState = {
  status: "idle" | "loading" | "ready" | "error";
  text?: string;
  error?: string;
};

type GenericSpecsHistoryExpandedContentProps = {
  revision: SpecsHistoryRevision;
  previousRevision: SpecsHistoryRevision | null;
  revisionDiffs: Record<string, RevisionDiffState>;
  isExpanded: boolean;
  specName: string;
  publishingRevisionId: string | null;
  onBack: (specName: string) => void;
  onPublish: (revision: SpecsHistoryRevision) => void;
};

const styles = createStyles({
  diffPre: {
    m: 0,
    p: 1.5,
    borderRadius: 1,
    bgcolor: palette.grey.lightBackground,
    border: `1px solid ${palette.grey.lightGrey}`,
    fontFamily: "monospace",
    fontSize: 12,
    lineHeight: 1.5,
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
});

function getDiffDisplayText({
  diffState,
  isExpanded,
  compareRevisionId,
  compareAgainstBaseSpecs,
}: {
  diffState: RevisionDiffState | undefined;
  isExpanded: boolean;
  compareRevisionId: string | null;
  compareAgainstBaseSpecs: boolean;
}) {
  if (
    diffState?.status === "loading" ||
    (isExpanded && (compareRevisionId || compareAgainstBaseSpecs) && !diffState)
  ) {
    return "Loading diff...";
  }

  if (diffState?.status === "error") {
    return diffState.error || "Failed to load diff";
  }

  if (diffState?.status === "ready") {
    return diffState.text;
  }

  if (compareRevisionId || compareAgainstBaseSpecs) {
    return "Expand to load diff";
  }

  return "No source revision to compare";
}

export default function GenericSpecsHistoryExpandedContent({
  revision,
  previousRevision,
  revisionDiffs,
  isExpanded,
  specName,
  publishingRevisionId,
  onBack,
  onPublish,
}: GenericSpecsHistoryExpandedContentProps) {
  const diffState = revisionDiffs[revision.revisionId];
  const fallbackRevisionId = previousRevision?.revisionId ?? null;
  const compareRevisionId = revision.sourceRevisionId ?? fallbackRevisionId;
  const compareAgainstBaseSpecs = !compareRevisionId;
  const diffDisplayText = getDiffDisplayText({
    diffState,
    isExpanded,
    compareRevisionId,
    compareAgainstBaseSpecs,
  });

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Stack spacing={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" color="text.secondary">
            Revision diff
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onBack(specName)}
          >
            Back to editor
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ minWidth: 96 }}
          >
            Current hash
          </Typography>
          <Typography variant="body2">{revision.revisionId}</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ minWidth: 96 }}
          >
            Source hash
          </Typography>
          <Typography variant="body2">
            {revision.sourceRevisionId || "—"}
          </Typography>
        </Stack>
        {!revision.sourceRevisionId && fallbackRevisionId ? (
          <Stack direction="row" spacing={1}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 96 }}
            >
              Compared to
            </Typography>
            <Typography variant="body2">
              Previous revision ({fallbackRevisionId})
            </Typography>
          </Stack>
        ) : null}
        {compareAgainstBaseSpecs ? (
          <Stack direction="row" spacing={1}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 96 }}
            >
              Compared to
            </Typography>
            <Typography variant="body2">Base specs</Typography>
          </Stack>
        ) : null}
        <Box component="pre" sx={styles.diffPre}>
          {diffDisplayText}
        </Box>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="outlined"
            size="small"
            disabled={publishingRevisionId === revision.revisionId}
            onClick={() => onPublish(revision)}
          >
            {publishingRevisionId === revision.revisionId
              ? "Publication..."
              : "Publier en prod"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
